import React, { useMemo } from 'react';
import { EventPageType } from '@graphql/generated-base';

import { LazyCoupleStory } from '../../../../widgets/CoupleStory/CoupleStory.lazy';
import { LazyFaqList } from '../../../../widgets/FaqList/FaqList.lazy';
import { LazyScheduleList } from '../../../../widgets/ScheduleList/ScheduleList.lazy';
import { LazyTravelList } from '../../../../widgets/TravelList/TravelList.lazy';
import { LazyWeddingPartyList } from '../../../../widgets/WeddingPartyList/WeddingPartyList.lazy';
import { LazyTidbitsList } from '../../../../widgets/TidbitsList/TidbitsList.lazy';
import type { LayoutAlohaProps } from '../../../LayoutAloha';
import type { ColumnV2Props } from '@withjoy/joykit';
import { Box, Flex } from '@withjoy/joykit';
import { EventLogisticsBlock } from '../../../../components/EventLogisticsBlock';
import { LazyRegistryList } from '@apps/guest/packages/layout-engine/widgets/RegistryList/RegistryList.lazy';
import { CONTENT_BLOCK_MAX_WIDTH } from '../../LayoutAloha.theme';
import { FitContentToHeight } from '../FitContentToHeight';
import { useArtSafeZone } from '../../hooks/useArtSafeZone';
import { CtaButtons } from '@apps/guest/packages/layout-engine/components/CtaButtons/CtaButtons';
import { LazyApps } from '@apps/guest/packages/layout-engine/widgets/Apps/Apps.lazy';
import { ChunkErrorBoundary } from '@shared/components/ChunkErrorBoundary';
import { LazyCustom } from '@apps/guest/packages/layout-engine/widgets/Custom/Custom.lazy';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useGuestPageContentRedesign } from '@apps/guest/hooks/useGuestPageContentRedesign';
import { getEffectiveScheduleLayout } from '@shared/components/ScheduleLayoutSelector/ScheduleLayoutSelector.utils';
import { findEventPageByPageSlug } from '../../../layout.utils';
import { toCanonicalMigratedSlug } from '@shared/utils/migratedCustomPages';
import { EVENT_PAGE_SLUGS } from '@apps/guest/shared/constants/eventPageSlugs';

const WelcomePageWidget = React.forwardRef<HTMLDivElement, LayoutAlohaProps>(({ event, ...restProps }, ref) => {
  const { eventInfo } = useEventInfo();
  return (
    <Box
      ref={ref}
      height={'100vh'}
      display={'flex'}
      alignItems="center"
      maxWidth={CONTENT_BLOCK_MAX_WIDTH}
      marginX="auto"
      {...restProps}
    >
      <EventLogisticsBlock
        location={event.info.location}
        date={event.info.finalizedDate}
        hideEventCountdown={eventInfo?.settings?.hideEventCountdown ?? false}
        pages={event.pages}
      >
        <CtaButtons />
      </EventLogisticsBlock>
    </Box>
  );
});

export type PreparedPages =
  | EventPageType.app
  | EventPageType.registry
  | EventPageType.travel
  | EventPageType.schedule
  | EventPageType.faq
  | EventPageType.vip
  | EventPageType.story
  | EventPageType.tidbits
  | EventPageType.welcome
  | EventPageType.custom;

export const pageColumnProps: Readonly<Record<PreparedPages, ColumnV2Props>> = {
  [EventPageType.app]: { span: [10], offset: [1] },
  [EventPageType.faq]: { span: [10, 6], offset: [1, 3] },
  [EventPageType.registry]: { span: [10], offset: [1] },
  [EventPageType.schedule]: { span: [12, 10, 8], offset: [0, 1, 2] },
  [EventPageType.story]: { span: [10, 8], offset: [1, 2] },
  [EventPageType.tidbits]: { span: [10, 8], offset: [1, 2] },
  [EventPageType.travel]: { span: [10], offset: [1] },
  [EventPageType.vip]: { span: [10, 10], offset: [1, 1] },
  [EventPageType.welcome]: {},
  [EventPageType.custom]: {},
};

export const pageWidgets: Readonly<Record<PreparedPages, React.ForwardRefExoticComponent<LayoutAlohaProps & React.RefAttributes<HTMLDivElement>>>> = {
  [EventPageType.custom]: React.forwardRef<HTMLDivElement, LayoutAlohaProps>(
    ({ event, eventHandle, isWelcomePage, isAccommodations, pageId, pageSlug, pageTitle, ...restProps }, ref) => {
      // Art safe zone (Aloha welcome hero) — keeps content clear of the theme art.
      const { safeZoneStyles, safeZoneMaxHeight } = useArtSafeZone();
      // Held as one element rather than rebuilt on every render. This component subscribes to the
      // feature flags and the theming context through `useArtSafeZone`, so it re-renders when
      // neither its props nor the safe zone have moved — seven times during a single nav on mobile,
      // each one re-rendering the ~260 components of a custom page. A stable element lets React
      // skip the whole subtree on those renders. Must sit above the early return below so the hook
      // order never changes.
      const customPageBody = useMemo(
        () => (
          <ChunkErrorBoundary section="guest/widget/custom">
            <LazyCustom
              pageId={pageId || ''}
              pageSlug={pageSlug || ''}
              eventId={event.id}
              pageTitle={pageTitle}
              eventHandle={eventHandle}
              pages={event.pages}
            />
          </ChunkErrorBoundary>
        ),
        [pageId, pageSlug, event.id, event.pages, pageTitle, eventHandle],
      );
      // Migrated registrylinks: render the registry-links listing without the custom-page
      // wrapper so it visually matches the legacy `EventPageType.registry` branch below
      // (the padding/maxWidth wrapper otherwise drifts between flag-on and flag-off).
      const isMigratedRegistryPage = toCanonicalMigratedSlug(pageSlug) === EVENT_PAGE_SLUGS.Registry;
      if (isMigratedRegistryPage) {
        return (
          <ChunkErrorBoundary section="guest/widget/registryList">
            <LazyRegistryList
              ref={ref}
              eventId={event.id}
              eventHandle={eventHandle}
            />
          </ChunkErrorBoundary>
        );
      }
      const isMigratedSchedulePage = toCanonicalMigratedSlug(pageSlug) === EVENT_PAGE_SLUGS.Schedule;
      const body = customPageBody;
      return (
        <>
          {isWelcomePage ? (
            <Box
              ref={ref}
              data-testid={'page-custom-welcome'}
              minHeight={'100vh'}
              display={'flex'}
              flexDirection={'column'}
              alignItems="center"
              justifyContent={'center'}
              maxWidth={CONTENT_BLOCK_MAX_WIDTH}
              marginX="auto"
              width={'100%'}
              {...restProps}
              // Reserve the art safe band (vw padding); FitContentToHeight then
              // scales the content to fit it. No-op when no safe zone applies.
              style={{ ...(restProps as { style?: React.CSSProperties }).style, ...safeZoneStyles }}
            >
              <FitContentToHeight maxHeight={safeZoneMaxHeight}>{body}</FitContentToHeight>
            </Box>
          ) : isAccommodations ? (
            <Flex
              ref={ref}
              data-testid={'page-custom'}
              alignItems="center"
              justifyContent={'center'}
              maxWidth={CONTENT_BLOCK_MAX_WIDTH}
              width={'100%'}
              marginX="auto"
            >
              {body}
            </Flex>
          ) : (
            <Box
              ref={ref}
              data-testid={'page-custom'}
              display={'flex'}
              alignItems="center"
              margin={'0 auto'}
              justifyContent={'center'}
              maxWidth={CONTENT_BLOCK_MAX_WIDTH}
              paddingTop={isMigratedSchedulePage ? 0 : { _: 7, sm2: 10 }}
              paddingBottom={{ _: 0, sm2: 10 }}
            >
              {body}
            </Box>
          )}
        </>
      );
    },
  ),
  [EventPageType.app]: React.forwardRef<HTMLDivElement, LayoutAlohaProps>((_props, ref) => (
    <Box
      ref={ref}
      data-testid={'page-apps'}
      display={'flex'}
      alignItems="center"
      margin={'0 auto'}
      justifyContent={'center'}
      maxWidth={CONTENT_BLOCK_MAX_WIDTH}
      paddingTop={{ _: 7, sm2: 10 }}
      paddingBottom={{ _: 7, sm2: 10 }}
    >
      <ChunkErrorBoundary section="guest/widget/apps">
        <LazyApps data-testid={'page-app'} />
      </ChunkErrorBoundary>
    </Box>
  )),
  [EventPageType.faq]: React.forwardRef<HTMLDivElement, LayoutAlohaProps>((props, ref) => (
    <ChunkErrorBoundary section="guest/widget/faqList">
      <LazyFaqList
        ref={ref}
        data-testid={'page-faq'}
        eventHandle={props.eventHandle}
        margin={'0 auto'}
        maxWidth={CONTENT_BLOCK_MAX_WIDTH}
        paddingTop={{ _: 7, sm2: 10 }}
        paddingBottom={{ _: 0, sm2: 10 }}
      />
    </ChunkErrorBoundary>
  )),
  [EventPageType.registry]: React.forwardRef<HTMLDivElement, LayoutAlohaProps>((props, ref) => (
    <ChunkErrorBoundary section="guest/widget/registryList">
      <LazyRegistryList
        ref={ref}
        eventId={props.event.id}
        eventHandle={props.eventHandle}
        margin={'0 auto'}
        maxWidth={CONTENT_BLOCK_MAX_WIDTH}
        paddingTop={{
          _: 7,
          lg: 56,
        }}
      />
    </ChunkErrorBoundary>
  )),
  [EventPageType.schedule]: React.forwardRef<HTMLDivElement, LayoutAlohaProps>((props, ref) => {
    const schedulePage = findEventPageByPageSlug(props.event.pages, 'schedule');
    const { enabled: isRedesignEnabled } = useGuestPageContentRedesign();
    const effectiveLayoutType = getEffectiveScheduleLayout(schedulePage?.layoutType, isRedesignEnabled);
    return (
      <ChunkErrorBoundary section="guest/widget/scheduleList">
        <LazyScheduleList
          ref={ref}
          data-testid={'page-schedule'}
          eventHandle={props.eventHandle}
          variant={'stack'}
          eventId={props.event.id}
          margin={'0 auto'}
          maxWidth={CONTENT_BLOCK_MAX_WIDTH}
          layoutType={effectiveLayoutType}
          pageId={schedulePage?.id}
        />
      </ChunkErrorBoundary>
    );
  }),
  [EventPageType.story]: React.forwardRef<HTMLDivElement, LayoutAlohaProps>((props, ref) => (
    <ChunkErrorBoundary section="guest/widget/coupleStory">
      <LazyCoupleStory
        ref={ref}
        data-testid={'page-story'}
        stackByDefault={true}
        margin={'0 auto'}
        maxWidth={CONTENT_BLOCK_MAX_WIDTH}
        paddingTop={{ _: 7, sm2: 10 }}
        paddingBottom={{ _: 0, sm2: 10 }}
        eventHandle={props.eventHandle}
        eventPages={props.event.pages}
      />
    </ChunkErrorBoundary>
  )),
  [EventPageType.tidbits]: React.forwardRef<HTMLDivElement, LayoutAlohaProps>((props, ref) => (
    <ChunkErrorBoundary section="guest/widget/tidbitsList">
      <LazyTidbitsList
        ref={ref}
        data-testid={'page-tidbits'}
        eventHandle={props.eventHandle}
        stackByDefault={true}
        margin={'0 auto'}
        maxWidth={CONTENT_BLOCK_MAX_WIDTH}
        paddingTop={{ _: 7, sm2: 10 }}
        paddingBottom={{ _: 0, sm2: 10 }}
      />
    </ChunkErrorBoundary>
  )),
  [EventPageType.travel]: React.forwardRef<HTMLDivElement, LayoutAlohaProps>((props, ref) => (
    <ChunkErrorBoundary section="guest/widget/travelList">
      <LazyTravelList
        ref={ref}
        data-testid={'page-travel'}
        eventId={props.event.id}
        eventHandle={props.eventHandle}
        useMiniNav={false}
        pages={props.event.pages}
        condensed={true}
        margin={'0 auto'}
        maxWidth={CONTENT_BLOCK_MAX_WIDTH}
        paddingTop={{ _: 7, sm2: 10 }}
        paddingBottom={{ _: 0, sm2: 10 }}
      />
    </ChunkErrorBoundary>
  )),
  [EventPageType.vip]: React.forwardRef<HTMLDivElement, LayoutAlohaProps>((props, ref) => (
    <ChunkErrorBoundary section="guest/widget/weddingPartyList">
      <LazyWeddingPartyList
        eventId={props.event.id}
        eventHandle={props.eventHandle}
        ref={ref}
        data-testid={'page-vip'}
        graphicAccent={undefined}
        columnCount={6}
        margin={'0 auto'}
        maxWidth={CONTENT_BLOCK_MAX_WIDTH}
        paddingTop={{ _: 7, sm2: 10 }}
        paddingBottom={{ _: 0, sm2: 10 }}
      />
    </ChunkErrorBoundary>
  )),
  [EventPageType.welcome]: WelcomePageWidget,
};
